/** @shell standard @header bar 
 * Dev hub (INDEX 0) — API target switch, initial screen override, and future dev tools.
 */
import React, { useCallback, useMemo } from 'react';
import { StyleSheet, Text, View, Pressable } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { androidScaleFont } from '../../shared/theme/androidUiParity';
import { useAuthNavigation } from '../../shared/navigation/useAuthNavigation';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../../components/common/ScreenHeader';
import { ScreenShell } from '../../shared/theme/studio/ScreenShell';
import { ApiTargetSwitchBar } from './ApiTargetSwitchBar';
import { InitialScreenTool } from './InitialScreenTool';
import { SubscriptionMenuProbeTool } from './SubscriptionMenuProbeTool';
import { ApiCallHistoryTool } from './ApiCallHistoryTool';
import { LoadMain } from '../../components/load-main/LoadMain';
import { environment } from '../../shared/config/environment';
import { isDevHubClient } from '../environment/devHub';

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    root: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scroll: {
      flex: 1,
    },
    content: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.xxl,
      gap: appTheme.spacing.lg,
    },
    summary: {
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: 1,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.xs,
    },
    summaryLabel: {
      ...appTheme.typography.captionSmall,
      color: c.textLight,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      fontWeight: '700',
    },
    summaryValue: {
      ...appTheme.typography.bodySmall,
      color: c.text,
    },
    card: {
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: 1,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
      overflow: 'hidden',
    },
    navLinkBtn: {
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: 1,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.lg,
      alignItems: 'center',
    },
    navLinkText: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.primary,
    },
    loaderPreviewCard: {
      borderRadius: appTheme.borderRadius.lg,
      borderWidth: 1,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
      padding: appTheme.spacing.lg,
      alignItems: 'center',
      gap: appTheme.spacing.md,
    },
    loaderPreviewTitle: {
      ...appTheme.typography.captionSmall,
      color: c.textLight,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      fontWeight: '800',
      textAlign: 'center',
    },
    loaderPreviewBody: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: androidScaleFont(18),
    },
  });
}

export function DevToolsScreen() {
  const nav = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const header_top_padding = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const on_back = useCallback(() => {
    if (nav.canGoBack()) {
      nav.goBack();
      return;
    }
    (nav as { navigate: (name: string) => void }).navigate('Main');
  }, [nav]);

  if (!isDevHubClient()) {
    return null;
  }

  return (
    <ScreenShell colors={colors}>
      <View style={styles.root}>
        <ScreenHeader title="Dev Tools" onBack={on_back} scrollY={scrollY} />

        <Animated.ScrollView
          style={styles.scroll}
          contentContainerStyle={[styles.content, { paddingTop: header_top_padding, paddingBottom: insets.bottom + appTheme.spacing.xxl }]}
          keyboardShouldPersistTaps="handled"
          onScroll={onScroll}
          scrollEventThrottle={16}>
        <View style={styles.summary}>
          <Text style={styles.summaryLabel}>Active session</Text>
          <Text style={styles.summaryValue}>API: {environment.IDENTIFIER}</Text>
          <Text style={styles.summaryValue} numberOfLines={2}>
            {environment.API_URL}
          </Text>
        </View>

        <View style={styles.card}>
          <ApiTargetSwitchBar />
        </View>

        <View style={styles.card}>
          <InitialScreenTool />
        </View>

        <View style={styles.card}>
          <SubscriptionMenuProbeTool />
        </View>

        <View style={styles.loaderPreviewCard}>
          <LoadMain
            variant="sphere"
            size={96}
            color={colors.primary}
            loopDurationMs={2200}
            accessibilityLabel="Sphere loader preview"
          />
          <View>
            <Text style={styles.loaderPreviewTitle}>Sphere loader preview</Text>
            <Text style={styles.loaderPreviewBody}>
              Permanent review instance for the LoadMain sphere variant.
            </Text>
          </View>
        </View>

        <Pressable
          onPress={() => nav.navigate('Settings')}
          style={styles.navLinkBtn}
          accessibilityRole="button"
          accessibilityLabel="Open Settings">
          <Text style={styles.navLinkText}>Settings (background test)</Text>
        </Pressable>

        <View style={styles.card}>
          <ApiCallHistoryTool />
        </View>
        </Animated.ScrollView>
      </View>
    </ScreenShell>
  );
}

export default DevToolsScreen;
